import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree as TreeIcon,
  Edit as EditIcon,
  ExpandLess,
  ExpandMore,
  RestartAlt as ResetIcon,
  Visibility as PreviewIcon,
} from '@mui/icons-material';

import {
  type OutputLevel,
  type OutputStructure,
} from '../../lib/outputStructure';
import { describePkStrategy } from '../../lib/syntheticPk';

interface PreservedSection {
  content: string;
  position: string;
  label?: string | null;
}

interface SyntheticPkFormula {
  target_table?: string;
  target_column?: string;
  strategy?: string;
  inputs?: string[];
}

interface OutputStructureTabProps {
  outputStructure: OutputStructure;
  /** Open the structure editor (e.g. EditDraftDialog). */
  onEdit?: () => void;
  /** Reset to the deterministically-derived baseline structure. */
  onReset?: () => void;
  /** Optional `definition.synthetic_pk_formulas` map. When supplied,
   *  levels whose key column is platform-synthesized show a plain
   *  "ID is built by …" annotation. */
  syntheticPkFormulas?: Record<string, SyntheticPkFormula> | null;
}

const OutputStructureTab: React.FC<OutputStructureTabProps> = ({
  outputStructure,
  onEdit,
  onReset,
  syntheticPkFormulas,
}) => {
  // Index PK formulas by `target_column` for fast lookup. Multiple
  // tables may share a column name, so we don't disambiguate by table
  // here — the level rendering only needs the strategy/inputs.
  const formulaByColumn = React.useMemo(() => {
    const map: Record<string, SyntheticPkFormula> = {};
    Object.values(syntheticPkFormulas || {}).forEach((formula) => {
      if (formula?.target_column) {
        map[formula.target_column] = formula;
      }
    });
    return map;
  }, [syntheticPkFormulas]);
  // Path-based expansion state so sibling inserts/removes don't shift
  // what's expanded (which index-based state would do). A path is the
  // '/'-joined chain of element_name's from root to the level.
  const collectAllPaths = (nodes: OutputLevel[], prefix: string[] = []): string[] => {
    const out: string[] = [];
    for (const n of nodes) {
      const path = [...prefix, n.element_name];
      out.push(path.join('/'));
      out.push(...collectAllPaths(n.children || [], path));
    }
    return out;
  };

  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(
    () => new Set(collectAllPaths(outputStructure.levels)),
  );
  const [showPreserved, setShowPreserved] = useState(false);

  const togglePath = (path: string) => {
    setExpandedPaths((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const { levels, preserved_sections: sections = [], namespaces = {} } = outputStructure;

  // Recursive renderer — a parent may have multiple sibling children of
  // different element types.
  const renderLevel = (
    level: OutputLevel,
    depth: number,
    parentPath: string[],
  ): React.ReactNode => {
    const path = [...parentPath, level.element_name];
    const pathKey = path.join('/');
    const isExpanded = expandedPaths.has(pathKey);
    return (
      <Box
        key={pathKey}
        sx={{
          ml: `${depth * 24}px`,
          mb: 0.5,
          borderLeft: '2px solid',
          borderColor: depth === 0 ? 'primary.main' : 'grey.300',
          pl: 1.5,
          py: 0.5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small" onClick={() => togglePath(pathKey)}>
            {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
          <Typography variant="body2" fontWeight={600}>
            {level.element_name}
          </Typography>
          {level.ref_id && (
            <Chip label={level.ref_id} size="small" variant="outlined" color="info" />
          )}
        </Stack>

        <Collapse in={isExpanded}>
          <Box sx={{ pl: 4, pt: 0.5 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              Key: <strong>{level.key_column}</strong>
              {level.parent_key && <> (parent: {level.parent_key})</>}
            </Typography>
            {(() => {
              const formula = level.key_column ? formulaByColumn[level.key_column] : undefined;
              if (!formula) return null;
              const phrase = describePkStrategy(formula.strategy, formula.inputs);
              if (!phrase) return null;
              return (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ fontStyle: 'italic' }}>
                  ID is built by {phrase}.
                </Typography>
              );
            })()}
            {level.fields && level.fields.length > 0 && (
              <Typography variant="caption" color="text.secondary" component="div">
                Fields: {level.fields.join(', ')}
              </Typography>
            )}
            {level.attributes && Object.keys(level.attributes).length > 0 && (
              <Typography variant="caption" color="text.secondary" component="div">
                Attributes:{' '}
                {Object.entries(level.attributes)
                  .map(([k, v]) => `${k}="${v}"`)
                  .join(', ')}
              </Typography>
            )}
            {(level.children || []).map((child) =>
              renderLevel(child, depth + 1, path),
            )}
          </Box>
        </Collapse>
      </Box>
    );
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <TreeIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Output Structure
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Chip
            label={`Format: ${outputStructure.format.toUpperCase()}`}
            size="small"
            variant="outlined"
          />
          {outputStructure.root_element && (
            <Chip
              label={`Root: <${outputStructure.root_element}>`}
              size="small"
              variant="outlined"
            />
          )}
          {onEdit && (
            <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEdit} sx={{ ml: 1 }}>
              Edit
            </Button>
          )}
          {onReset && (
            <Button size="small" variant="text" startIcon={<ResetIcon />} onClick={onReset}>
              Reset to Default
            </Button>
          )}
        </Stack>
      </Stack>

      {/* Hierarchy tree — siblings may have different element types. */}
      {levels.map((level) => renderLevel(level, 0, []))}

      {levels.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No hierarchy levels defined. Run Interpret to populate.
        </Typography>
      )}

      {/* Preserved sections */}
      {sections.length > 0 && (
        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => setShowPreserved(!showPreserved)}
          >
            <Tooltip title="Template sections embedded verbatim in output">
              <PreviewIcon fontSize="small" color="action" />
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              Preserved Sections: {sections.length} section
              {sections.length !== 1 ? 's' : ''} from template
            </Typography>
            {showPreserved ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Stack>
          <Collapse in={showPreserved}>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {sections.map((section, idx) => (
                <Box
                  key={idx}
                  sx={{
                    p: 1,
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'grey.200',
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Chip
                      label={section.label || 'Unnamed'}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      label={section.position}
                      size="small"
                      color="default"
                    />
                    <Typography variant="caption" color="text.secondary">
                      {section.content.length.toLocaleString()} chars
                    </Typography>
                  </Stack>
                  <Box
                    sx={{
                      maxHeight: 120,
                      overflow: 'auto',
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                      color: 'text.secondary',
                    }}
                  >
                    {section.content.slice(0, 500)}
                    {section.content.length > 500 && '...'}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      {/* Namespaces */}
      {Object.keys(namespaces).length > 0 && (
        <Box sx={{ mt: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" component="div">
            Namespaces:{' '}
            {Object.entries(namespaces)
              .map(([prefix, uri]) => (prefix ? `${prefix}="${uri}"` : `xmlns="${uri}"`))
              .join(', ')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default OutputStructureTab;
